import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import Button from "../Button";
import Toast from "../Toast";
import { SUBMIT_USER_KYC_URL, postData } from "../../utils/api";
import { getLoginDetails, storeLoginDetails } from "../../utils/storage";
import "../../containers/Logs/kyc-questionnaire.css";
import "../../containers/Kyc/kyc.css";
import AbcatLogo from "../../icons/abcattechwhite.png";
import BG from "../../icons/bg.png";

const QUESTION_TWO_OPTIONS = [
  { id: 1, label: "Scrap yard" },
  { id: 2, label: "Junk yard" },
  { id: 3, label: "Auto recycler" },
  { id: 4, label: "Muffler shop" },
  { id: 5, label: "Individuals" },
  { id: 6, label: "Other" },
];

const YES_NO = ["yes", "no"];
const QUESTION_COPY = {
  1: "How long have you been in the catalytic converter purchasing business? Describe your knowledge about legal sourcing requirements.",
  2: "Where do you purchase your catalytic converters from? (Check all that apply)",
  3: "Have you ever knowingly purchased stolen or unlawfully obtained catalytic converters?",
  4: "Are you aware of and do you comply with all federal, state, and local record-keeping requirements for purchasing catalytic converters?",
  5: "Do you make cash payments for catalytic converters and, if so, do you comply with anti-money laundering regulations?",
  6: "Do you maintain records of all cash transactions related to catalytic converter purchases?",
  7: "Have you or any member of your organization ever been arrested or convicted of a crime related to theft, fraud, or dealing in stolen property?",
  8: "Have you ever been denied service by any catalytic converter processor or smelter in the past 12 months?",
};

const DEFAULT_FORM = {
  yearsInBusiness: "",
  legalSourceKnowledge: "",
  secondQuery: [],
  thirdQuery: "",
  fourthQuery: "",
  fifthQuery: "",
  sixthQuery: "",
  seventhQuery: "",
  eighthQuery: "",
  fourthQueryDesc: "",
  fifthQueryDesc: "",
  sixthQueryDesc: "",
  seventhQueryDesc: "",
  eighthQueryDesc: "",
  fullName: "",
  businessName: "",
  certificationAccepted: false,
};

const formatDateMMDDYYYY = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const QuestionCard = ({ num, title, isOpen, onToggle, children }) => (
  <div className="kyc-q__card">
    <button type="button" className="kyc-q__card-head" onClick={onToggle}>
      <span className="kyc-q__badge">{num}</span>
      <span className="kyc-q__card-title">{title}</span>
      <span className="kyc-q__chevron">{isOpen ? "▲" : "▼"}</span>
    </button>
    {isOpen ? <div className="kyc-q__card-body">{children}</div> : null}
  </div>
);

/**
 * @param {object} props
 * @param {() => void} [props.onSuccess] — if set, called after successful submit instead of navigate+reload
 * @param {'page' | 'embed'} [props.variant] — embed: no full-page min-height wrapper (for modal)
 * @param {boolean} [props.showBranding] — show hero branding above form (default true for page, false for embed-only; overlay passes true)
 */
export default function KycForm({ onSuccess, variant = "page", showBranding = true }) {
  const history = useHistory();
  const loginDetails = useMemo(() => getLoginDetails() || {}, []);
  const ownerNameFromLogin = useMemo(
    () =>
      [loginDetails.firstName, loginDetails.lastName]
        .filter(Boolean)
        .join(" ")
        .trim(),
    [loginDetails]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    ...DEFAULT_FORM,
    fullName: ownerNameFromLogin,
    businessName:
      loginDetails.organisationName ||
      loginDetails.companyName ||
      loginDetails.company_name ||
      "",
  });
  const today = useMemo(() => formatDateMMDDYYYY(new Date()), []);
  const [toastParams, setToastParams] = useState({
    message: "",
    type: "",
    id: "",
  });
  const [open, setOpen] = useState(() => {
    const state = {};
    for (let i = 1; i <= 8; i += 1) state[i] = true;
    return state;
  });

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSecondQuery = (value) => {
    setForm((prev) => {
      const exists = prev.secondQuery.includes(value);
      return {
        ...prev,
        secondQuery: exists
          ? prev.secondQuery.filter((item) => item !== value)
          : [...prev.secondQuery, value],
      };
    });
  };

  const showError = (message) => {
    setToastParams({
      type: "ERROR",
      message,
      id: Date.now(),
    });
  };

  const validateForm = () => {
    const years = Number(form.yearsInBusiness);
    if (!Number.isFinite(years) || years < 0) {
      showError("Please enter a valid value for years in business.");
      return false;
    }
    if (!form.legalSourceKnowledge.trim()) {
      showError("Please describe your legal sourcing knowledge.");
      return false;
    }
    if (!form.secondQuery.length) {
      showError("Please select at least one source in question 2.");
      return false;
    }
    if (!YES_NO.includes(form.thirdQuery)) {
      showError("Please answer question 3.");
      return false;
    }
    if (!YES_NO.includes(form.fourthQuery)) {
      showError("Please answer question 4.");
      return false;
    }
    if (!YES_NO.includes(form.fifthQuery)) {
      showError("Please answer question 5.");
      return false;
    }
    if (!YES_NO.includes(form.sixthQuery)) {
      showError("Please answer question 6.");
      return false;
    }
    if (!YES_NO.includes(form.seventhQuery)) {
      showError("Please answer question 7.");
      return false;
    }
    if (!YES_NO.includes(form.eighthQuery)) {
      showError("Please answer question 8.");
      return false;
    }
    if (!form.fullName.trim()) {
      showError("Please enter full name.");
      return false;
    }
    if (!form.businessName.trim()) {
      showError("Please enter business name.");
      return false;
    }
    if (!form.certificationAccepted) {
      showError("Please certify that all statements are true and correct.");
      return false;
    }
    return true;
  };

  const isFormComplete = useMemo(() => {
    const years = Number(form.yearsInBusiness);
    const hasBaseAnswers =
      Number.isFinite(years) &&
      years >= 0 &&
      Boolean(form.legalSourceKnowledge.trim()) &&
      form.secondQuery.length > 0 &&
      YES_NO.includes(form.thirdQuery) &&
      YES_NO.includes(form.fourthQuery) &&
      YES_NO.includes(form.fifthQuery) &&
      YES_NO.includes(form.sixthQuery) &&
      YES_NO.includes(form.seventhQuery) &&
      YES_NO.includes(form.eighthQuery) &&
      Boolean(form.fullName.trim()) &&
      Boolean(form.businessName.trim()) &&
      form.certificationAccepted;

    if (!hasBaseAnswers) return false;

    if (form.sixthQuery === "no" && !form.sixthQueryDesc.trim()) return false;
    if (form.seventhQuery === "yes" && !form.seventhQueryDesc.trim()) return false;
    if (form.eighthQuery === "yes" && !form.eighthQueryDesc.trim()) return false;

    return true;
  }, [form]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const payload = {
        yearsInBusiness: Number(form.yearsInBusiness),
        legalSourceKnowledge: form.legalSourceKnowledge.trim(),
        secondQuery: form.secondQuery,
        thirdQuery: form.thirdQuery,
        fourthQuery: form.fourthQuery,
        fifthQuery: form.fifthQuery,
        sixthQuery: form.sixthQuery,
        seventhQuery: form.seventhQuery,
        eighthQuery: form.eighthQuery,
        fourthQueryDesc: form.fourthQueryDesc.trim(),
        fifthQueryDesc: form.fifthQueryDesc.trim(),
        sixthQueryDesc: form.sixthQueryDesc.trim(),
        seventhQueryDesc: form.seventhQueryDesc.trim(),
        eighthQueryDesc: form.eighthQueryDesc.trim(),
        fullName: form.fullName.trim(),
        businessName: form.businessName.trim(),
      };

      await postData(SUBMIT_USER_KYC_URL, payload);
      const updatedLoginDetails = { ...getLoginDetails(), isKYCSubmitted: true };
      storeLoginDetails(updatedLoginDetails);
      setToastParams({
        type: "SUCCESS",
        message: "KYC submitted successfully.",
        id: Date.now(),
      });
      if (typeof onSuccess === "function") {
        onSuccess();
      } else {
        history.push("/converters/identify");
        window.location.reload();
      }
    } catch (e) {
      showError(
        e && e.response && e.response.data && e.response.data.error
          ? e.response.data.error
          : "Failed to submit KYC. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderYesNo = (field) => (
    <div className="kyc-q__yn-row">
      {YES_NO.map((value) => (
        <label key={value} className="kyc-q__yn kyc-edit__choice">
          <input
            type="radio"
            name={field}
            checked={form[field] === value}
            onChange={() => setField(field, value)}
          />
          <span
            className={`kyc-q__radio ${
              form[field] === value ? "kyc-q__radio--on" : ""
            }`}
          >
            {form[field] === value ? <span className="kyc-q__radio-dot" /> : null}
          </span>
          {value === "yes" ? "Yes" : "No"}
        </label>
      ))}
    </div>
  );

  const rootClass =
    variant === "embed" ? "kyc-form-page kyc-form-page--embed" : "kyc-form-page";

  return (
    <div className={rootClass}>
      {showBranding ? (
        <div
          className="kyc-brand"
          style={{
            backgroundImage:
              "linear-gradient(rgba(10, 14, 25, 0.78), rgba(10, 14, 25, 0.85)), url(" +
              BG +
              ")",
          }}
        >
          <img src={AbcatLogo} alt="ABCAT Tech" className="kyc-brand__logo" />
          <h1 className="kyc-brand__title">Know Your Client</h1>
          <p className="kyc-brand__subtitle">Required compliance questionnaire</p>
        </div>
      ) : null}
      <form className="kyc-q kyc-edit" onSubmit={onSubmit}>
        <div className="kyc-q__banner" role="status">
          <span>KYC required before using other app features</span>
        </div>

        <h2 className="kyc-q__title">KYC Questionnaire</h2>

        <QuestionCard
          num={1}
          title={QUESTION_COPY[1]}
          isOpen={open[1]}
          onToggle={() => setOpen((prev) => ({ ...prev, 1: !prev[1] }))}
        >
          <input
            className="kyc-q__input"
            type="number"
            min="0"
            placeholder="Years in business"
            value={form.yearsInBusiness}
            onChange={(e) => setField("yearsInBusiness", e.target.value)}
          />
          <textarea
            className="kyc-q__input kyc-q__input--textarea"
            rows={4}
            placeholder="Describe your knowledge about legal sourcing requirements."
            value={form.legalSourceKnowledge}
            onChange={(e) => setField("legalSourceKnowledge", e.target.value)}
          />
        </QuestionCard>

        <QuestionCard
          num={2}
          title={QUESTION_COPY[2]}
          isOpen={open[2]}
          onToggle={() => setOpen((prev) => ({ ...prev, 2: !prev[2] }))}
        >
          <div className="kyc-q__options">
            {QUESTION_TWO_OPTIONS.map((opt) => {
              const isChecked = form.secondQuery.includes(opt.id);
              return (
                <label key={opt.id} className="kyc-q__option-row kyc-edit__choice">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleSecondQuery(opt.id)}
                  />
                  <span className={`kyc-q__check ${isChecked ? "kyc-q__check--on" : ""}`}>
                    {isChecked ? <span className="kyc-q__check-dot" /> : null}
                  </span>
                  {opt.label}
                </label>
              );
            })}
          </div>
        </QuestionCard>

        <QuestionCard
          num={3}
          title={QUESTION_COPY[3]}
          isOpen={open[3]}
          onToggle={() => setOpen((prev) => ({ ...prev, 3: !prev[3] }))}
        >
          {renderYesNo("thirdQuery")}
        </QuestionCard>

        <QuestionCard
          num={4}
          title={QUESTION_COPY[4]}
          isOpen={open[4]}
          onToggle={() => setOpen((prev) => ({ ...prev, 4: !prev[4] }))}
        >
          {renderYesNo("fourthQuery")}
        </QuestionCard>

        <QuestionCard
          num={5}
          title={QUESTION_COPY[5]}
          isOpen={open[5]}
          onToggle={() => setOpen((prev) => ({ ...prev, 5: !prev[5] }))}
        >
          {renderYesNo("fifthQuery")}
        </QuestionCard>

        <QuestionCard
          num={6}
          title={QUESTION_COPY[6]}
          isOpen={open[6]}
          onToggle={() => setOpen((prev) => ({ ...prev, 6: !prev[6] }))}
        >
          {renderYesNo("sixthQuery")}
          {form.sixthQuery === "no" ? (
            <textarea
              className="kyc-q__input kyc-q__input--textarea"
              rows={3}
              placeholder="Description"
              value={form.sixthQueryDesc}
              onChange={(e) => setField("sixthQueryDesc", e.target.value)}
            />
          ) : null}
        </QuestionCard>

        <QuestionCard
          num={7}
          title={QUESTION_COPY[7]}
          isOpen={open[7]}
          onToggle={() => setOpen((prev) => ({ ...prev, 7: !prev[7] }))}
        >
          {renderYesNo("seventhQuery")}
          {form.seventhQuery === "yes" ? (
            <textarea
              className="kyc-q__input kyc-q__input--textarea"
              rows={3}
              placeholder="Description"
              value={form.seventhQueryDesc}
              onChange={(e) => setField("seventhQueryDesc", e.target.value)}
            />
          ) : null}
        </QuestionCard>

        <QuestionCard
          num={8}
          title={QUESTION_COPY[8]}
          isOpen={open[8]}
          onToggle={() => setOpen((prev) => ({ ...prev, 8: !prev[8] }))}
        >
          {renderYesNo("eighthQuery")}
          {form.eighthQuery === "yes" ? (
            <textarea
              className="kyc-q__input kyc-q__input--textarea"
              rows={3}
              placeholder="Description"
              value={form.eighthQueryDesc}
              onChange={(e) => setField("eighthQueryDesc", e.target.value)}
            />
          ) : null}
        </QuestionCard>

        <div className="kyc-q__cert">
          <div className="kyc-q__cert-head">
            <span className="kyc-q__cert-title">Certification</span>
          </div>
          <p className="kyc-edit__cert-note">
            I certify that the information provided in this questionnaire is true,
            accurate, and complete to the best of my knowledge. I understand that
            providing false information may result in termination of services and
            potential legal consequences.
          </p>
          <div className="kyc-edit__cert-fields">
            <div>
              <div className="kyc-q__cert-field-label">
                Owner / Authorized Representative Name
              </div>
              <input
                className="kyc-q__input"
                type="text"
                value={form.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
              />
            </div>
            <div>
              <div className="kyc-q__cert-field-label">Business name</div>
              <input
                className="kyc-q__input"
                type="text"
                value={form.businessName}
                onChange={(e) => setField("businessName", e.target.value)}
              />
            </div>
            <div>
              <div className="kyc-q__cert-field-label">Date</div>
              <input className="kyc-q__input" type="text" value={today} readOnly />
            </div>
          </div>
          <label className="kyc-edit__cert-check">
            <input
              type="radio"
              name="certificationAccepted"
              checked={form.certificationAccepted}
              onChange={(e) => setField("certificationAccepted", e.target.checked)}
            />
            <span
              className={`kyc-q__radio ${
                form.certificationAccepted ? "kyc-q__radio--on" : ""
              }`}
            >
              {form.certificationAccepted ? <span className="kyc-q__radio-dot" /> : null}
            </span>
            <span>
              I certify that all above statements are true and correct, and I agree
              to comply with all applicable laws and regulations.
            </span>
          </label>
        </div>

        <div className="kyc-edit__submit">
          <Button className="btn" disabled={isSubmitting || !isFormComplete}>
            {isSubmitting ? "Submitting..." : "Submit KYC"}
          </Button>
        </div>
      </form>
      <Toast type={toastParams.type} message={toastParams.message} id={toastParams.id} />
    </div>
  );
}
